/**
 * GainReductionMeter Component
 * 
 * Vertical bar meter for displaying compressor gain reduction in real-time.
 * Shows dB scale with color coding (green/yellow/red).
 */

import { useEffect, useRef } from 'react';
import './GainReductionMeter.css';

/**
 * Props for GainReductionMeter component
 */
export interface GainReductionMeterProps {
  /** Current gain reduction in dB (0 to -60) */
  gainReduction: number;
  /** Width of the meter in pixels */
  width?: number;
  /** Height of the meter in pixels */
  height?: number;
}

/**
 * GainReductionMeter component
 * 
 * Features:
 * - Vertical bar display
 * - dB scale markings (0, -6, -12, -18, -24, -30)
 * - Color coding: green (0 to -6dB), yellow (-6 to -12dB), red (-12dB+)
 * - Smooth 60fps animation
 * - Peak hold indicator
 * 
 * @param props - Component props
 * @returns GainReductionMeter component
 */
export function GainReductionMeter({
  gainReduction,
  width = 40,
  height = 120,
}: GainReductionMeterProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const peakHoldRef = useRef<number>(0);
  const peakHoldTimeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    /**
     * Draw the meter
     */
    const draw = () => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw background
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, width, height);

      // Draw scale markings
      drawScale(ctx, width, height);

      // Clamp gain reduction to valid range (0 to -60)
      const clampedGR = Math.max(-60, Math.min(0, gainReduction));

      // Update peak hold
      const now = Date.now();
      if (clampedGR < peakHoldRef.current) {
        peakHoldRef.current = clampedGR;
        peakHoldTimeRef.current = now;
      } else if (now - peakHoldTimeRef.current > 1500) {
        // Decay peak hold after 1.5 seconds
        peakHoldRef.current = Math.min(0, peakHoldRef.current + 0.5);
      }

      // Draw meter bar
      drawMeterBar(ctx, width, height, clampedGR);

      // Draw peak hold indicator
      if (peakHoldRef.current < -0.1) {
        drawPeakHold(ctx, width, height, peakHoldRef.current);
      }

      // Continue animation
      animationFrameRef.current = requestAnimationFrame(draw);
    };

    // Start animation
    draw();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gainReduction, width, height]);

  return (
    <div className="gain-reduction-meter">
      <canvas ref={canvasRef} className="gain-reduction-meter-canvas" />
      <div className="gain-reduction-meter-label">GR</div>
    </div>
  );
}

/**
 * Draw scale markings on the meter
 */
function drawScale(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const scaleMarks = [0, -6, -12, -18, -24, -30];
  
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 1;
  ctx.font = '9px monospace';
  ctx.fillStyle = '#666';
  ctx.textAlign = 'right';

  scaleMarks.forEach((db) => {
    const y = dbToY(db, height);
    
    // Draw tick mark
    ctx.beginPath();
    ctx.moveTo(width - 8, y);
    ctx.lineTo(width, y);
    ctx.stroke();
    
    // Draw label (only for major marks)
    if (db === 0 || db === -12 || db === -24) {
      ctx.fillText(`${db}`, width - 10, y + 3);
    }
  });
}

/**
 * Draw the meter bar with color coding
 */
function drawMeterBar(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  gainReduction: number
): void {
  const barWidth = width - 12;
  const barX = 2;

  // Calculate bar height
  const barHeight = Math.abs(gainReduction / 60) * height;
  const barY = height - barHeight;

  // Create gradient for color coding
  const gradient = ctx.createLinearGradient(0, height, 0, 0);
  gradient.addColorStop(0, '#4CAF50');    // Green (0 to -6dB)
  gradient.addColorStop(0.1, '#4CAF50');
  gradient.addColorStop(0.2, '#FFC107');  // Yellow (-6 to -12dB)
  gradient.addColorStop(0.3, '#FF5722');  // Red (-12dB+)
  gradient.addColorStop(1, '#FF5722');

  // Draw bar
  ctx.fillStyle = gradient;
  ctx.fillRect(barX, barY, barWidth, barHeight);

  // Draw border
  ctx.strokeStyle = '#444';
  ctx.lineWidth = 1;
  ctx.strokeRect(barX, 0, barWidth, height);
}

/**
 * Draw peak hold indicator
 */
function drawPeakHold(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  peakGR: number
): void {
  const y = dbToY(peakGR, height);
  const barWidth = width - 12;
  const barX = 2;

  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(barX, y);
  ctx.lineTo(barX + barWidth, y);
  ctx.stroke();
}

/**
 * Convert dB value to Y coordinate
 */
function dbToY(db: number, height: number): number {
  // Map 0dB to bottom, -60dB to top
  const normalized = Math.abs(db) / 60;
  return height - (normalized * height);
}
